import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

const Serverless = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>AWS API Gateway & Serverless Integrations - limkc</title>
        <meta name="description" content="Learn about AWS API Gateway & Serverless Integrations with real-world examples." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
  
      <div className="w-full h-full overflow-auto">
        {/* Banner Image - Full Width */}
        <div className="w-full h-48 overflow-hidden" style={{ marginTop: 'var(--navbar-height)' }}>
          <img
            src="/IMAGE/serverless.png"
            alt="Serverless Architecture"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Content - Centered */}
        <div className="w-full flex justify-center">
          <div className="py-6 transition-all duration-300 w-full"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-primary)',
              maxWidth: '1200px',
              paddingLeft: '1rem',
              paddingRight: '1rem'
            }}>

            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>AWS API Gateway & Serverless Integrations</h1>
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>
            Serverless computing allows developers to build scalable, cost-efficient applications without managing infrastructure.  
            AWS Lambda and API Gateway form the foundation of event-driven, serverless architectures, enabling seamless API integrations and backend automation.
          </p>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Why Use Serverless?</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Serverless computing is ideal for modern cloud applications because:
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li>No infrastructure management – AWS handles provisioning, scaling, and maintenance.</li>
            <li>Pay-as-you-go pricing – You are charged only for execution time, reducing costs.</li>
            <li>High scalability – Automatically scales to meet demand, without manual intervention.</li>
            <li>Event-driven architecture – Ideal for real-time processing, webhooks, and microservices.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Key AWS Serverless Components</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>API Gateway</b> – Manages RESTful, WebSocket, and HTTP APIs for serverless backends.</li>
            <li><b>AWS Lambda</b> – Executes functions on demand, triggered by API calls, S3 events, or DynamoDB streams.</li>
            <li><b>Step Functions</b> – Orchestrates complex workflows without managing state.</li>
            <li><b>EventBridge & SQS</b> – Enables event-driven messaging and decoupled architectures.</li>
            <li><b>DynamoDB</b> – Serverless NoSQL database designed for low-latency, high-scale workloads.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Real World Experience</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            I built a real-time AI chatbot using AWS Lambda, API Gateway, and DynamoDB.  
            The biggest challenges included handling concurrency limits, optimizing cold starts, and securing API endpoints.  
            The final solution used Lambda provisioned concurrency, WebSockets for real-time messaging, and IAM policies for access control.
          </p>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Common Challenges & Solutions</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>Cold Start Delays</b> – Use provisioned concurrency to keep functions warm.</li>
            <li><b>Rate Limiting & Security</b> – Configure WAF, API Gateway throttling, and IAM permissions.</li>
            <li><b>Latency Issues</b> – Optimize functions with minimal dependencies and short execution times.</li>
            <li><b>Observability</b> – Use AWS X-Ray and CloudWatch Logs for monitoring and debugging.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Basic Serverless API Example</h2>
          <pre className="p-4 rounded-md mt-2 text-sm" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>
  {`
  # Define a simple AWS Lambda function
  import json
  
  def lambda_handler(event, context):
      return {
          "statusCode": 200,
          "body": json.dumps("Hello from Lambda!")
      }
  `}
          </pre>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Basic API Gateway Configuration (Terraform)</h2>
          <pre className="p-4 rounded-md mt-2 text-sm" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>
  {`
  resource "aws_api_gateway_rest_api" "example_api" {
    name        = "MyServerlessAPI"
    description = "API Gateway for serverless Lambda functions"
  }
  
  resource "aws_api_gateway_resource" "example_resource" {
    rest_api_id = aws_api_gateway_rest_api.example_api.id
    parent_id   = aws_api_gateway_rest_api.example_api.root_resource_id
    path_part   = "hello"
  }
  
  resource "aws_lambda_permission" "api_permission" {
    statement_id  = "AllowAPIGatewayInvoke"
    action        = "lambda:InvokeFunction"
    function_name = aws_lambda_function.hello_lambda.function_name
    principal     = "apigateway.amazonaws.com"
  }
  `}
          </pre>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Best Practices for API Gateway</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li>Enable caching in API Gateway to reduce Lambda execution costs.</li>
            <li>Use JWT or IAM authentication to secure API endpoints.</li>
            <li>Enable WAF (Web Application Firewall) to prevent DDoS attacks.</li>
            <li>Monitor API performance with CloudWatch Metrics & Logs.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Related Topics</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><a href="/topics/kubernetes" className="text-blue-500 hover:underline">Kubernetes & Containerized Workloads</a></li>
            <li><a href="/topics/cloud-management" className="text-blue-500 hover:underline">Cloud Management & Multi-Cloud Strategy</a></li>
          </ul>
  
          <button 
            onClick={() => navigate("/projects")} 
            className="mt-6 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
          >
            ← Back to Topics
          </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Serverless;
